import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Settings, Image, MessageSquare, LogOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { logger } from '../lib/logger';

interface ContentItem {
  id: string;
  section: string;
  key: string;
  value: string;
  type: 'text' | 'image' | 'richtext';
  updated_at: string;
}

interface Message {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
  read: boolean;
}

export function AdminPanel({ onClose }: { onClose: () => void }) {
  const [activeTab, setActiveTab] = useState<'content' | 'messages'>('content');
  const [content, setContent] = useState<ContentItem[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { success, error: showError } = useNotifications();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchContent();
    fetchMessages();
  }, []);

  // ... (autres fonctions restent identiques)

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#ef2543] border-t-transparent mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full h-full md:h-[90vh] md:w-[90vw] md:max-w-6xl md:rounded-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center">
            {isMobile && (
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="mr-4 text-gray-600 hover:text-[#ef2543]"
              >
                {isSidebarOpen ? <ChevronLeft /> : <ChevronRight />}
              </button>
            )}
            <h2 className="text-xl md:text-2xl font-bold">Administration</h2>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center text-gray-600 hover:text-[#ef2543]"
          >
            <LogOut className="w-5 h-5 mr-2" />
            <span className="hidden md:inline">Déconnexion</span>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div
            className={`${
              isSidebarOpen ? 'w-full md:w-64' : 'w-0'
            } flex-shrink-0 border-r bg-gray-50 transition-all duration-300 ${
              isMobile ? 'absolute inset-y-0 left-0 z-20' : 'relative'
            }`}
          >
            <nav className="p-4">
              <button
                className={`w-full flex items-center p-3 rounded-lg mb-2 ${
                  activeTab === 'content'
                    ? 'bg-[#ef2543] text-white'
                    : 'hover:bg-gray-200'
                }`}
                onClick={() => {
                  setActiveTab('content');
                  if (isMobile) setIsSidebarOpen(false);
                }}
              >
                <Settings className="w-5 h-5 mr-3" />
                Contenu
              </button>
              <button
                className={`w-full flex items-center p-3 rounded-lg ${
                  activeTab === 'messages'
                    ? 'bg-[#ef2543] text-white'
                    : 'hover:bg-gray-200'
                }`}
                onClick={() => {
                  setActiveTab('messages');
                  if (isMobile) setIsSidebarOpen(false);
                }}
              >
                <MessageSquare className="w-5 h-5 mr-3" />
                Messages
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="ml-auto px-2 py-1 text-xs bg-white text-[#ef2543] rounded-full">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {activeTab === 'content' ? (
                <div className="grid gap-6 md:grid-cols-2">
                  {content.map((item) => (
                    <div key={item.id} className="border rounded-lg p-4 bg-white shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-medium text-gray-700">{item.key}</h3>
                        <span className="text-sm text-gray-500">{item.section}</span>
                      </div>
                      
                      {editingContent?.id === item.id ? (
                        <div className="space-y-2">
                          {item.type === 'image' ? (
                            <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                              <input
                                type="text"
                                value={editingContent.value}
                                onChange={(e) => setEditingContent({
                                  ...editingContent,
                                  value: e.target.value
                                })}
                                className="flex-1 p-2 border rounded"
                                placeholder="URL de l'image"
                              />
                              <Image className="w-5 h-5 text-gray-500" />
                            </div>
                          ) : (
                            <textarea
                              value={editingContent.value}
                              onChange={(e) => setEditingContent({
                                ...editingContent,
                                value: e.target.value
                              })}
                              rows={3}
                              className="w-full p-2 border rounded"
                            />
                          )}
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => setEditingContent(null)}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => updateContent(item.id, editingContent.value)}
                              className="px-4 py-2 bg-[#ef2543] text-white rounded hover:bg-[#ef2543]/80"
                            >
                              Enregistrer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col md:flex-row md:items-start justify-between">
                          <div className="flex-1 mb-2 md:mb-0">
                            {item.type === 'image' ? (
                              <div className="relative h-32 bg-gray-100 rounded">
                                <img
                                  src={item.value}
                                  alt={item.key}
                                  className="h-full w-auto object-contain"
                                />
                              </div>
                            ) : (
                              <p className="text-gray-600">{item.value}</p>
                            )}
                          </div>
                          <button
                            onClick={() => setEditingContent(item)}
                            className="text-[#ef2543] hover:text-[#ef2543]/80"
                          >
                            Modifier
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`border rounded-lg p-4 bg-white shadow-sm ${
                        !message.read ? 'bg-gray-50' : ''
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between mb-2">
                        <h3 className="font-medium flex items-center">
                          {message.subject}
                          {!message.read && (
                            <span className="ml-2 px-2 py-1 text-xs bg-[#ef2543] text-white rounded-full">
                              Nouveau
                            </span>
                          )}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">
                        De: {message.name} 
                        <span className="block md:inline md:ml-2">({message.email})</span>
                      </p>
                      <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                      {!message.read && (
                        <button
                          onClick={() => markMessageAsRead(message.id)}
                          className="mt-2 text-[#ef2543] hover:text-[#ef2543]/80"
                        >
                          Marquer comme lu
                        </button>
                      )}
                    </div>
                  ))}
                  {messages.length === 0 && (
                    <p className="text-center text-gray-500">Aucun message</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}